import { Form } from "@unform/web";
import { FileText, Pencil, X } from "lucide-react";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Zoom, toast } from "react-toastify";
import { useSelector } from "react-redux";
import Preview from "~/components/Preview";
import Input from "~/components/RegisterForm/Input";
import Textarea from "~/components/RegisterForm/Textarea";
import InputLine from "~/components/RegisterForm/InputLine";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import FormHeader from "~/components/RegisterForm/FormHeader";
import RegisterFormMenu from "~/components/RegisterForm/Menu";
import FormLoading from "~/components/RegisterForm/FormLoading";
import SelectPopover from "~/components/RegisterForm/SelectPopover";
import { FullGridContext } from "../../..";
import { JobDescriptionContext } from "..";
import { getRegistries, handleUpdatedFields } from "~/functions";
import api from "~/services/api";

export const InputContext = createContext({});

export default function PagePreview({
  access,
  id,
  defaultFormType = "preview",
}) {
  const auth = useSelector((state) => state.auth);
  const { handleOpened, setOpened, successfullyUpdated, setSuccessfullyUpdated } =
    useContext(FullGridContext);
  const { departments } = useContext(JobDescriptionContext);
  const [pageData, setPageData] = useState({
    name: "",
    description: "",
    department_id: null,
    department: null,
    loaded: false,
  });
  const [registry, setRegistry] = useState({
    created_by: null,
    created_at: null,
    updated_by: null,
    updated_at: null,
    canceled_by: null,
    canceled_at: null,
  });
  const [formType, setFormType] = useState(defaultFormType);
  const [fullscreen, setFullscreen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("general");
  const generalForm = useRef();

  function handleCloseForm() {
    if (!successfullyUpdated) {
      toast("Changes discarted!", { autoClose: 1000 });
    }
    handleOpened(null);
  }

  function handleGeneralFormSubmit(data) {
    if (successfullyUpdated) {
      toast("No need to be saved!", {
        autoClose: 1000,
        type: "info",
        transition: Zoom,
      });
      return;
    }

    const payload = {
      name: data.name,
      description: data.description || "",
      department_id: data.department_id || null,
    };
    const selectedDepartment =
      departments.find(
        (dept) => String(dept.id) === String(payload.department_id)
      ) || pageData.department || null;

    if (!selectedDepartment) {
      toast("Select a department to proceed.", {
        autoClose: 2000,
        type: "error",
      });
      return;
    }

    if (id === "new") {
      const createPayload = {
        department: selectedDepartment ? { id: selectedDepartment.id } : null,
        name: payload.name,
        description: payload.description,
      };
      api
        .post(`/jobdescriptions`, createPayload)
        .then((response) => {
          const newId = response.data?.id || null;
          setOpened(newId);
          setPageData({
            ...pageData,
            ...payload,
            department: selectedDepartment,
            department_id: selectedDepartment?.id || null,
          });
          setSuccessfullyUpdated(true);
          toast("Saved!", { autoClose: 1000 });
          handleOpened(null);
        })
        .catch((err) => {
          if (err?.response?.data?.error) {
            toast(err.response.data.error, { type: "error", autoClose: 3000 });
          }
        });
    } else {
      const updated = handleUpdatedFields(
        { ...payload, department: selectedDepartment },
        pageData
      );
      if (updated.length > 0) {
        const objUpdated = Object.fromEntries(updated);
        const updatePayload = {
          department: selectedDepartment ? { id: selectedDepartment.id } : null,
          name: objUpdated.name || pageData.name,
          description: objUpdated.description || pageData.description,
        };
        api
          .put(`/jobdescriptions/${id}`, updatePayload)
          .then(() => {
            setPageData({
              ...pageData,
              ...objUpdated,
              department: selectedDepartment,
            });
            setSuccessfullyUpdated(true);
            toast("Saved!", { autoClose: 1000 });
            handleOpened(null);
          })
          .catch((err) => {
            if (err?.response?.data?.error) {
              toast(err.response.data.error, { type: "error", autoClose: 3000 });
            }
          });
      }
    }
  }

  useEffect(() => {
    if (id === "new") {
      setFormType("full");
      const firstDepartment = departments[0] || null;
      setPageData((prev) => ({
        ...prev,
        department_id: firstDepartment?.id || null,
        department: firstDepartment,
        loaded: true,
      }));
    } else {
      async function fetchJobDescription() {
        try {
          const { data } = await api.get(`/jobdescriptions/${id}`);
          const {
            created_by,
            created_at,
            updated_by,
            updated_at,
            canceled_by,
            canceled_at,
          } = data;
          const registries = await getRegistries({
            created_by,
            created_at,
            updated_by,
            updated_at,
            canceled_by,
            canceled_at,
          });
          setRegistry(registries);
          setPageData({
            ...data,
            department_id: data.department?.id || null,
            department: data.department || null,
            description: data.description,
            loaded: true,
          });
        } catch (err) {
          if (err?.response?.data?.error) {
            toast(err.response.data.error, { type: "error", autoClose: 3000 });
          }
        }
      }
      fetchJobDescription();
    }
  }, [id, departments]);

  const defaultDepartmentOption = pageData.department_id
    ? {
        value: pageData.department_id,
        label:
          departments.find(
            (dept) => String(dept.id) === String(pageData.department_id)
          )?.name || pageData.department?.name || "Department",
      }
    : null;

  return (
    <Preview formType={formType} fullscreen={fullscreen}>
      {pageData ? (
        formType === "preview" ? (
          <div className="border h-full rounded-xl overflow-hidden flex flex-col justify-start gap-1 overflow-y-scroll">
            <div className="relative bg-gray-100 h-16 px-4 py-2 flex flex-row items-start justify-start">
              <button
                onClick={() => setFormType("full")}
                className="absolute top-2 right-20 text-md font-bold bg-mila_orange text-white rounded-md p-1 px-2 h-6 flex flex-row items-center justify-center text-xs gap-1"
              >
                <Pencil size={16} color="#fff" /> Open
              </button>
              <button
                onClick={() => handleOpened(null)}
                className="absolute top-2 right-2 text-md font-bold bg-secondary rounded-md p-1 px-2 h-6 flex flex-row items-center justify-center text-xs gap-1"
              >
                <X size={16} /> Close
              </button>
              <h2 style={{ fontSize: 24 }}>{pageData.name}</h2>
            </div>
          </div>
        ) : (
          <div className="flex h-full flex-row items-start justify-between gap-4">
            <div className="flex flex-col items-center justify-between text-xs w-32 gap-4">
              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="general"
              >
                <FileText size={16} /> General
              </RegisterFormMenu>
            </div>
            <div className="border h-full rounded-xl overflow-hidden flex flex-1 flex-col justify-start">
              <div className="flex flex-col items-start justify-start text-sm overflow-y-scroll">
                <Form
                  ref={generalForm}
                  onSubmit={handleGeneralFormSubmit}
                  className="w-full"
                >
                  <InputContext.Provider
                    value={{
                      id,
                      generalForm,
                      setSuccessfullyUpdated,
                      fullscreen,
                      setFullscreen,
                      successfullyUpdated,
                      handleCloseForm,
                    }}
                  >
                    {id === "new" || pageData.loaded ? (
                      <>
                        <FormHeader
                          access={access}
                          title={pageData.name || "New Job Description"}
                          registry={registry}
                          InputContext={InputContext}
                          createText="Save"
                        />

                        <InputLineGroup
                          title="GENERAL"
                          activeMenu={activeMenu === "general"}
                        >
                          <InputLine title="Department">
                            <SelectPopover
                              name="department_id"
                              title="Department"
                              required
                              grow
                              isSearchable
                              options={departments.map((dept) => ({
                                value: dept.id,
                                label: dept.name,
                              }))}
                              defaultValue={defaultDepartmentOption}
                              InputContext={InputContext}
                            />
                          </InputLine>

                          <InputLine title="Job Description Information">
                            <Input
                              type="text"
                              name="name"
                              required
                              title="Name"
                              grow
                              defaultValue={pageData.name}
                              InputContext={InputContext}
                            />
                          </InputLine>

                          <InputLine title="Details">
                            <Textarea
                              name="description"
                              required
                              title="Job Description"
                              InputContext={InputContext}
                              grow
                              rows={10}
                              defaultValue={pageData.description}
                            />
                          </InputLine>
                        </InputLineGroup>
                      </>
                    ) : (
                      <FormLoading />
                    )}
                  </InputContext.Provider>
                </Form>
              </div>
            </div>
          </div>
        )
      ) : null}
    </Preview>
  );
}
